/**
 * SequencerPanel — Claude-style reasoning timeline (Jul 2026, rev 3).
 *
 * Active state: a quiet vertical timeline of recent agent steps (dot +
 * connector line, muted text) with a small black spinner at the bottom
 * carrying the current-activity phrase and elapsed time — exactly the
 * activity idiom of Claude's own "Thought process" display. No particle
 * canvas, no counters row, no progress bars, no boxed card.
 *
 * Done state: one quiet line ("Draft ready · 3:12") with a chevron
 * that expands per-agent rows.
 *
 * Language rule: agent activity is ALWAYS English; strip is LTR.
 * Data contract unchanged: { state, isArabic }.
 */
import React, { useEffect, useRef, useState } from 'react';

const AGENT_LABELS = {
  scribe: 'Scribe', examiner: 'Examiner', regis: 'Regis',
  counsel: 'Counsel', conductor: 'Conductor',
};

const PHRASES = {
  scribe_running: 'Drafting the agreement',
  examiner_running: 'Examining the draft for risk exposure',
  regis_running: 'Verifying statutory compliance',
  counsel_running: 'Consulting current regulations',
  conductor_running: 'Synthesizing the unified report',
  reviewing: 'Reviewing the draft against Saudi law',
  analyzing: 'Analyzing the contract',
  finishing: 'Finalizing',
  done_draft: 'Draft ready',
  done_review: 'Review complete',
};

function fmtClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${s}s`;
}

function glyphFor(status) {
  if (status === 'completed') return { ch: '✓', color: '#1F5A2D' };
  if (status === 'failed')    return { ch: '✗', color: '#911' };
  if (status === 'running')   return { ch: '●', color: '#1F1F1F' };
  return { ch: '○', color: '#999' };
}

function AgentRow({ name, info, liveNow }) {
  const status = info?.status || 'idle';
  const g = glyphFor(status);
  const startRef = useRef(null);
  if (status === 'running' && startRef.current == null) startRef.current = liveNow;
  const elapsed = status === 'running'
    ? Math.max(info?.elapsed_s || 0, startRef.current != null ? (liveNow - startRef.current) : 0)
    : (info?.elapsed_s || 0);
  const phrase = status === 'running'
    ? (PHRASES[`${name}_running`] || 'working')
    : status === 'completed' ? 'done'
    : status === 'failed' ? 'failed'
    : 'waiting';
  return (
    <div
      className="flex items-center gap-2.5 py-1 text-[12.5px]"
      data-testid={`sequencer-track-${name}`}
      data-agent-status={status}
      data-status={status}
    >
      <span aria-hidden style={{ color: g.color }} className="w-3 text-center tabular-nums">{g.ch}</span>
      <span className="hv-text font-medium">{AGENT_LABELS[name] || name}</span>
      <span className="hv-text-subtle flex-1 truncate">{phrase}</span>
      <span className="hv-text-subtle tabular-nums text-[11px]">{elapsed ? fmtClock(elapsed) : ''}</span>
    </div>
  );
}

/**
 * Progress Plan v2.1 (Jul 2026 redesign) — aligned checklist.
 * One 20px icon column threaded by a hairline rail; per-row time chips
 * right-aligned; details only under the active step (max 2, truncated);
 * elapsed lives in the header — no duplicate footer line.
 *
 * Calm-failure policy: a delivered run never shows red. A failed
 * sub-agent renders as a neutral "skipped this run" dash; the report
 * text itself discloses the reduced dimension. Red is reserved for
 * total failure (no result produced), which never reaches this panel.
 * Spinner CSS is self-contained — the legacy panel styles never mount
 * on the plan path.
 */
function PlanChecklist({ plan, done, open, setOpen, elapsedTotal }) {
  const steps = plan.steps || [];
  const finished = steps.filter((s) => s.status === 'done' || s.status === 'failed').length;
  const skipped = steps.filter((s) => s.status === 'failed').length;
  // Live Narration v3: the newest narration line of the active step is
  // promoted to a full-sentence working headline (fallback: the step
  // label's first segment). Real agent output, never invented copy.
  const activeStepForHl = steps.find((s) => s.status === 'active');
  const workingHeadline = (!done && activeStepForHl)
    ? (((activeStepForHl.details || []).slice(-1)[0]) || `${(activeStepForHl.label || '').split(' — ')[0]}…`)
    : null;

  if (done && !open) {
    return (
      <section className="my-3 max-w-[680px]" data-testid="sequencer-panel" data-plan="1" dir="ltr">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={false}
          className="w-full flex items-center gap-2.5 px-1 py-1.5 text-start"
          data-testid="plan-collapsed-toggle"
        >
          <span aria-hidden className="inline-block rounded-full flex-shrink-0" style={{ width: 7, height: 7, background: '#1F5A2D' }} />
          <span className="text-[13px] text-[#6E6B64]">
            Done — {steps.length} steps{skipped ? ` (${skipped} skipped)` : ''} · {fmtClock(elapsedTotal)}
          </span>
          <span className="text-[11px] text-[#BBBBBB]">expand</span>
        </button>
      </section>
    );
  }

  return (
    <section className="my-3 max-w-[680px]" data-testid="sequencer-panel" data-plan="1" dir="ltr">
      <style>{`.pc2-spin{width:12px;height:12px;border:1.5px solid rgba(0,0,0,0.15);border-top-color:#1a1a1a;border-radius:50%;animation:pc2r .8s linear infinite;display:inline-block}@keyframes pc2r{to{transform:rotate(360deg)}}`}</style>
      <div className="flex items-baseline justify-between gap-3 px-1 pb-2">
        <span className="text-[11px] uppercase text-[#9A968E]" style={{ letterSpacing: '0.14em' }}>
          {plan.task === 'full_draft' ? 'Drafting plan' : 'Review plan'}
        </span>
        <span className="text-[11.5px] text-[#9A968E] tabular-nums" data-testid="plan-counter">
          {done ? `Complete · ${fmtClock(elapsedTotal)}` : `Working · ${fmtClock(elapsedTotal)}`}
        </span>
      </div>
      {workingHeadline && (
        <div className="px-1 pb-3 text-[15px] font-medium text-[#1a1a1a]" data-testid="plan-headline">
          {workingHeadline}
        </div>
      )}
      <ol className="px-1" data-testid="plan-checklist">
        {steps.map((s, idx) => {
          // Aug 2026 — display-level closure: once the run is finished,
          // no row may keep a spinner or a pending circle. Stream drops
          // can eat completion events, and the polling fallback
          // bypasses the event reducer entirely — this panel is the
          // last line of defence. active→done, pending→neutral skip.
          const status = done && (s.status === 'active' || s.status === 'pending')
            ? (s.status === 'active' ? 'done' : 'failed')
            : s.status;
          const isSkip = status === 'failed';
          const label = isSkip ? `${(s.label || '').split(' — ')[0]} — skipped this run` : s.label;
          const color = status === 'pending' ? '#B5B2A9'
            : status === 'active' ? '#1a1a1a'
              : isSkip ? '#9A968E' : '#6E6B64';
          return (
            <li key={s.id} className="grid items-start" style={{ gridTemplateColumns: '20px minmax(0,1fr) 44px', columnGap: 10 }}>
              <span className="relative flex justify-center pt-[3px]">
                {status === 'done' && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1 }} aria-hidden>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
                {isSkip && <span className="text-[13px] leading-none text-[#B5B2A9] pt-[2px]" aria-hidden>–</span>}
                {status === 'active' && <span className="pc2-spin" aria-hidden />}
                {status === 'pending' && (
                  <span className="rounded-full" style={{ width: 11, height: 11, border: '1.5px solid #D2CFC6' }} aria-hidden />
                )}
                {idx < steps.length - 1 && (
                  <span aria-hidden className="absolute" style={{ top: 20, bottom: -4, left: '50%', width: 1, background: 'rgba(0,0,0,0.07)' }} />
                )}
              </span>
              <div className="min-w-0">
                <div className={`text-[13.5px] pb-2.5 ${status === 'active' ? 'font-medium' : ''}`} style={{ color }}>
                  {label}
                </div>
                {status === 'active' && (s.details || []).slice(-4).map((d, i, arr) => (
                  <div key={`${s.id}-${i}`} className="flex items-baseline gap-2 pb-1.5 min-w-0" style={{ opacity: 0.5 + 0.5 * ((i + 1) / arr.length) }}>
                    <span aria-hidden className="rounded-full flex-shrink-0" style={{ width: 3, height: 3, background: 'rgba(0,0,0,0.25)' }} />
                    <span className={`text-[12px] truncate ${i === arr.length - 1 ? 'text-[#5F5E5A]' : 'text-[#9A968E]'}`}>{d}</span>
                  </div>
                ))}
              </div>
              <span className="text-[11px] text-[#C4C1B8] text-right pt-[5px] tabular-nums">
                {status === 'done' && s.elapsed_s ? `${Math.round(s.elapsed_s)}s` : ''}
              </span>
            </li>
          );
        })}
      </ol>
      {done && (
        <button type="button" onClick={() => setOpen(false)} className="text-[11.5px] text-[#9A968E] px-1 pt-1" data-testid="plan-collapse">
          collapse
        </button>
      )}
    </section>
  );
}

export default function SequencerPanel({ state, isArabic = false }) {
  const active = Boolean(state && state.active);

  const mountRef = useRef(null);
  const [now, setNow] = useState(0);
  const agents = (state && state.agents) || {};
  const order = ['scribe', 'examiner', 'regis', 'counsel']
    .filter((n) => agents[n])
    .concat(Object.keys(agents).filter((n) => !['scribe', 'examiner', 'regis', 'counsel'].includes(n)));

  // Aug 2026 fix: the ticker was gated only on `active`, which stays
  // true after the run ends — so "Complete · 17:59" kept counting
  // while the result sat on screen. Stop the interval on completion
  // and freeze the displayed clock at the moment the run finished.
  const finishedClockRef = useRef(null);
  const runFinished = Boolean(state?.completed);
  useEffect(() => {
    if (!active || runFinished) return undefined;
    if (mountRef.current == null) mountRef.current = performance.now() / 1000;
    const id = setInterval(() => setNow(performance.now() / 1000), 1000);
    return () => clearInterval(id);
  }, [active, runFinished]);

  const [open, setOpen] = useState(false);

  if (!active) return null;

  let elapsedTotal = mountRef.current != null ? (now || performance.now() / 1000) - mountRef.current : 0;
  if (runFinished) {
    if (finishedClockRef.current == null) finishedClockRef.current = elapsedTotal;
    elapsedTotal = finishedClockRef.current;
  } else if (finishedClockRef.current != null) {
    finishedClockRef.current = null;
  }
  const isFullDraft = state.mode === 'full_draft' || Boolean(agents.scribe);
  const fanoutComplete = order.length > 0 && order.every((n) => {
    const s = agents[n]?.status;
    return s === 'completed' || s === 'failed';
  });
  const conductorStatus = state?.conductor?.status || 'waiting';
  const allDone = fanoutComplete && (conductorStatus === 'completed' || conductorStatus === 'failed');

  const words = Math.max(
    (state.scribe_draft_text || '').split(/\s+/).filter(Boolean).length,
    Object.values(state.draft_sections || {}).reduce((acc, s) => acc + (s.word_count || 0), 0),
  );

  const headline = (() => {
    if (allDone) return isFullDraft ? PHRASES.done_draft : PHRASES.done_review;
    if (conductorStatus === 'running') return `${PHRASES.conductor_running}…`;
    if (agents.scribe?.status === 'running') {
      return words > 0
        ? `${PHRASES.scribe_running}… ${words.toLocaleString('en-US')} words`
        : `${PHRASES.scribe_running}…`;
    }
    if (isFullDraft && (agents.examiner?.status === 'running' || agents.regis?.status === 'running')) {
      return `${PHRASES.reviewing}…`;
    }
    if (['counsel', 'examiner', 'regis'].some((n) => agents[n]?.status === 'running')) {
      return `${PHRASES.analyzing}…`;
    }
    return `${PHRASES.finishing}…`;
  })();

  const steps = state.last_steps || [];
  const visibleSteps = open ? steps : steps.slice(-4);

  // Progress Plan v2: when the orchestrator announced its checklist,
  // it IS the progress surface. Fallback below covers legacy streams.
  if (state.plan?.steps?.length) {
    return (
      <PlanChecklist
        plan={state.plan}
        done={Boolean(state.completed) || allDone}
        open={open}
        setOpen={setOpen}
        elapsedTotal={elapsedTotal}
      />
    );
  }

  if (allDone) {
    return (
      <section className="my-3 max-w-[760px]" data-testid="sequencer-panel" data-mode={isFullDraft ? 'full_draft' : 'full_review'} data-stage="synthesis" dir="ltr">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full flex items-center gap-2.5 px-1 py-1.5 text-start"
          data-testid="sequencer-panel-toggle"
        >
          <span aria-hidden className="inline-block rounded-full" style={{ width: 7, height: 7, background: '#1F5A2D' }} />
          <span className="text-[13px] hv-text-subtle" data-testid="sequencer-headline">
            {headline} · {fmtClock(elapsedTotal)}
          </span>
          <span className="flex-1" />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hv-text-subtle transition-transform" style={{ transform: open ? 'rotate(180deg)' : undefined }} aria-hidden>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        {open && (
          <div className="px-1 pt-1 pb-1.5 border-s ms-2 ps-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            {order.map((name) => (
              <AgentRow key={name} name={name} info={agents[name]} liveNow={now} />
            ))}
            <AgentRow name="conductor" info={state.conductor} liveNow={now} />
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      className="my-3 max-w-[760px]"
      data-testid="sequencer-panel"
      data-mode={isFullDraft ? 'full_draft' : 'full_review'}
      data-stage={fanoutComplete ? 'synthesis' : 'fan_out'}
      dir="ltr"
    >
      <style>{`
        @keyframes bynSpin { to { transform: rotate(360deg); } }
        .byn-spinner {
          width: 13px; height: 13px; border-radius: 50%;
          border: 1.5px solid rgba(0,0,0,0.15);
          border-top-color: #1a1a1a;
          animation: bynSpin 0.8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes bynShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        .byn-shimmer {
          background: linear-gradient(90deg, #9b9b95 35%, #1f1f1f 50%, #9b9b95 65%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: bynShimmer 2.2s linear infinite;
        }
      `}</style>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-start px-1"
        data-testid="sequencer-panel-toggle"
      >
        <ol className="space-y-0" data-testid="sequencer-ticker">
          {visibleSteps.map((s, i) => (
            <li key={`${i}-${s.content}`} className="flex items-stretch gap-2.5">
              <div className="flex flex-col items-center w-4 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#C9C6BD' }} />
                <span className="flex-1 w-px" style={{ background: 'rgba(0,0,0,0.08)' }} />
              </div>
              <span className="text-[12.5px] text-[#8a887f] pb-2.5 truncate">{s.content}</span>
            </li>
          ))}
          <li className="flex items-center gap-2.5 py-0.5">
            <span className="w-4 flex justify-center flex-shrink-0">
              <span className="byn-spinner" aria-hidden />
            </span>
            <span className="text-[13px] byn-shimmer" data-testid="sequencer-headline">{headline}</span>
            <span className="text-[11px] text-[#BBBBBB] tabular-nums">· {fmtClock(elapsedTotal)}</span>
          </li>
        </ol>
      </button>

      {open && (
        <div className="px-1 pt-2 pb-1.5 border-s ms-2 ps-3 mt-1" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          {order.map((name) => (
            <AgentRow key={name} name={name} info={agents[name]} liveNow={now} />
          ))}
          <AgentRow name="conductor" info={state.conductor} liveNow={now} />
        </div>
      )}
    </section>
  );
}
